import React from "react";
import { motion } from "framer-motion";
import { Camera } from "lucide-react";
import PageShell from "../components/PageShell";
import ImagePlaceholder from "../components/ImagePlaceholder";
import { aboutPortraitImage } from "../data/projects";

const About = () => {
  return (
    <PageShell testId="page-about">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-4 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="lg:col-span-5 lg:sticky lg:top-32"
          >
            <ImagePlaceholder
              src={aboutPortraitImage}
              alt="Portrait of STXP, photographer"
              ratio="aspect-[4/5]"
              rounded="rounded-[36px]"
              label="STXP · SELF"
              testId="about-portrait"
            />
          </motion.div>

          <div className="lg:col-span-7">
            <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-[#A8D2FA] mb-4">
              About me
            </div>
            <h1 className="font-fraunces text-4xl md:text-6xl font-light text-slate-900 dark:text-slate-100 leading-[1.05] text-balance">
              Hi, I&apos;m <em className="italic">Stasiia</em>.
            </h1>
            <p className="mt-6 font-manrope text-slate-500 dark:text-slate-400 dark:text-slate-500 max-w-2xl">
              Originally from Ukraine, currently based in the Netherlands.
            </p>

            <div className="mt-10 space-y-6 font-manrope text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-2xl">
              <p>
                I&apos;m at the beginning of my journey as a photographer,
                exploring different genres while building a portfolio and
                developing my own visual style. I enjoy creating conceptual
                projects, documenting events and architecture, and producing
                stock photography alongside my personal work. For me,
                photography is about creating images with intention rather than
                simply capturing what&apos;s in front of the camera.
              </p>
              <p>
                My interest in visual creation started early, when I used to
                make stop-motion videos with a small Sony camera. Since then,
                I&apos;ve explored different creative fields — video editing
                (which I did for many years), music production, digital
                drawing, and even tattoo art. Even though not all of them
                became long-term, they shaped how I see and approach visuals.
              </p>
              <p>
                I also have a background in both art and music, which strongly
                influences how I understand rhythm, composition, and emotion in
                my work. Photography feels like a point where everything comes
                together.
              </p>
              <p>
                What draws me in the most is the ability to control light,
                depth, and motion to create a certain feeling. I enjoy both the
                technical side of photography and the creative freedom it gives
                — for me, these two things are inseparable.
              </p>
              <p>
                I&apos;m especially interested in dynamic compositions, unusual
                perspectives, storytelling, and experimenting with light. I
                like images that feel alive and have a sense of movement or
                atmosphere.
              </p>
              <p>
                I&apos;m influenced by a mix of visual and audio media — from
                editorial and product photography to architecture, cinema,
                anime, and especially music. Music plays a huge role in how I
                experience and interpret emotion, and I try to bring that into
                my visual work.
              </p>
            </div>

            {/* Equipment */}
            <div className="mt-14" data-testid="about-equipment">
              <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-[#A8D2FA] mb-4">
                Equipment
              </div>
              <div className="glass rounded-[24px] p-6 flex items-center gap-5">
                <div className="w-12 h-12 rounded-2xl bg-[#E8F4FF] dark:bg-white/[0.08] border border-[#C6E2FF]/50 dark:border-white/10 grid place-items-center">
                  <Camera size={20} className="text-slate-700 dark:text-slate-200" />
                </div>
                <div>
                  <div className="font-fraunces text-2xl text-slate-900 dark:text-slate-100">Sony a6700</div>
                  <div className="font-manrope text-xs tracking-[0.2em] uppercase text-slate-500 dark:text-slate-400 dark:text-slate-500 mt-1">
                    Primary body · APS-C · Hybrid workflow
                  </div>
                </div>
              </div>
            </div>

            {/* Meta */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-6">
              <MetaItem label="Origin" value="Ukraine" />
              <MetaItem label="Based" value="Netherlands" />
              <MetaItem label="Working since" value="2026" />
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

const MetaItem = ({ label, value }) => (
  <div>
    <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-slate-400 dark:text-slate-500">
      {label}
    </div>
    <div className="font-fraunces text-xl text-slate-900 dark:text-slate-100 mt-1.5">{value}</div>
  </div>
);

export default About;
